import { useEffect, useState } from "react";
import { Sidebar } from "../../components/sidebar";
import { Wrapper } from "../../components/wrapper";
import { Snackbar } from "../../components/snackbar";
import CategoryIcon from "@mui/icons-material/Category";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import WarningIcon from "@mui/icons-material/Warning";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Spinner } from "../../components/spinner";
import { getAllBrands, getBrandStats, createBrand, updateBrand, deleteBrand } from "../../services/brand";
import { DEFAULT_SNACK } from "../../utils/snacks";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";

const COLORS = ["#3b82f6", "#8b5cf6", "#ec4899", "#f59e0b", "#10b981"];

export const Brands = () => {
  const [brands, setBrands] = useState([]);
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [newBrandName, setNewBrandName] = useState("");
  const [snack, setSnackbar] = useState(DEFAULT_SNACK);
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [brandsData, statsData] = await Promise.all([
        getAllBrands(),
        getBrandStats(),
      ]);
      setBrands(Array.isArray(brandsData) ? brandsData : []);
      setStats(Array.isArray(statsData) ? statsData : []);
      setLoading(false);
    } catch (error) {
      console.error("Error general:", error);
      setError("Error al cargar los datos");
      setLoading(false);
    }
  };

  const handleAddBrand = async () => {
    if (!newBrandName.trim()) {
      setSnackbar({
        visible: true,
        severity: "error",
        message: "El nombre de la marca es requerido",
      });
      setTimeout(() => setSnackbar(DEFAULT_SNACK), 3000);
      return;
    }

    try {
      const response = await createBrand({ name: newBrandName.trim() });
      
      if (response.success) {
        setShowAddModal(false);
        setNewBrandName("");
        await fetchData();
        setSnackbar({
          visible: true,
          severity: "success",
          message: "Marca creada exitosamente",
        });
        setTimeout(() => setSnackbar(DEFAULT_SNACK), 3000);
      } else {
        setSnackbar({
          visible: true,
          severity: "error",
          message: response.message || "Error al crear marca",
        });
        setTimeout(() => setSnackbar(DEFAULT_SNACK), 3000);
      }
    } catch (error) {
      console.error("Error adding brand:", error);
      setSnackbar({
        visible: true,
        severity: "error",
        message: "Error al crear marca",
      });
      setTimeout(() => setSnackbar(DEFAULT_SNACK), 3000);
    }
  };

  const openEditModal = (brand) => {
    setSelectedBrand(brand);
    setNewBrandName(brand.name);
    setShowEditModal(true);
  };

  const handleEditBrand = async () => {
    if (!newBrandName.trim()) {
      setSnackbar({
        visible: true,
        severity: "error",
        message: "El nombre de la marca es requerido",
      });
      setTimeout(() => setSnackbar(DEFAULT_SNACK), 3000);
      return;
    }

    try {
      const response = await updateBrand(selectedBrand._id, { name: newBrandName.trim() });
      
      if (response.success) {
        setShowEditModal(false);
        setNewBrandName("");
        setSelectedBrand(null);
        await fetchData();
        setSnackbar({
          visible: true,
          severity: "success",
          message: "Marca actualizada exitosamente",
        });
        setTimeout(() => setSnackbar(DEFAULT_SNACK), 3000);
      } else {
        setSnackbar({
          visible: true,
          severity: "error",
          message: response.message || "Error al actualizar marca",
        });
        setTimeout(() => setSnackbar(DEFAULT_SNACK), 3000);
      }
    } catch (error) {
      console.error("Error updating brand:", error);
      setSnackbar({
        visible: true,
        severity: "error",
        message: "Error al actualizar marca",
      });
      setTimeout(() => setSnackbar(DEFAULT_SNACK), 3000);
    }
  };

  const openDeleteModal = (brand) => {
    setSelectedBrand(brand);
    setShowDeleteModal(true);
  };

  const handleDeleteBrand = async () => {
    try {
      const response = await deleteBrand(selectedBrand._id);
      
      if (response.success) {
        setShowDeleteModal(false);
        setSelectedBrand(null);
        await fetchData();
        setSnackbar({
          visible: true,
          severity: "success",
          message: "Marca eliminada exitosamente",
        });
        setTimeout(() => setSnackbar(DEFAULT_SNACK), 3000);
      } else {
        setSnackbar({
          visible: true,
          severity: "error",
          message: response.message || "Error al eliminar marca",
        });
        setTimeout(() => setSnackbar(DEFAULT_SNACK), 3000);
      }
    } catch (error) {
      console.error("Error deleting brand:", error);
      setSnackbar({
        visible: true,
        severity: "error",
        message: "Error al eliminar marca",
      });
      setTimeout(() => setSnackbar(DEFAULT_SNACK), 3000);
    }
  };

  const filteredBrands = Array.isArray(brands)
    ? brands.filter((brand) =>
        brand?.name?.toLowerCase().includes(search.toLowerCase())
      )
    : [];

  const totalPages = Math.ceil(filteredBrands.length / itemsPerPage);
  const startIndex = currentPage * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedBrands = filteredBrands.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(0);
  }, [search]);

  if (loading) {
    return (
      <div className="flex min-h-screen">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center p-4 bg-sidebar-bg">
          <Wrapper>
            <Spinner />
          </Wrapper>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-sidebar-bg">
      <Sidebar />
      <div className="flex-1 flex flex-col p-4">
        <Wrapper>
          <div className="flex-1 p-6 lg:p-8">
            <div className="max-w-7xl mx-auto">
              <div className="mb-8 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center">
                      <CategoryIcon className="text-white" fontSize="small" />
                    </div>
                    <h1 className="text-3xl font-bold text-white">
                      Marcas de Vehículos
                    </h1>
                  </div>
                  <p className="text-gray-400 text-sm mt-1">
                    Catálogo de marcas y estadísticas
                  </p>
                </div>
                <button
                  onClick={() => setShowAddModal(true)}
                  className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200 flex items-center gap-2 shadow-sm hover:shadow-md"
                >
                  <AddIcon fontSize="small" />
                  Agregar Marca
                </button>
              </div>

              {error && (
                <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              )}

              {stats.length > 0 && (
                <div className="mb-8 bg-sidebar-bg rounded-xl border border-sidebar-accent p-6">
                  <h2 className="text-xl font-bold text-white mb-4">
                    Vehículos por Marca
                  </h2>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={stats.slice(0, 10)}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis
                        dataKey="brand"
                        stroke="#9ca3af"
                        tick={{ fill: "#9ca3af", fontSize: 12 }}
                      />
                      <YAxis
                        stroke="#9ca3af"
                        tick={{ fill: "#9ca3af", fontSize: 12 }}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "#1f2937",
                          border: "1px solid #374151",
                          borderRadius: "8px",
                          color: "#fff",
                        }}
                      />
                      <Bar dataKey="count" radius={[8, 8, 0, 0]}>
                        {stats.slice(0, 10).map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={COLORS[index % COLORS.length]}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              <div className="mb-6">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
                    <SearchIcon
                      className="text-gray-500"
                      sx={{ fontSize: 20 }}
                    />
                  </div>
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Buscar marca..."
                    className="w-full pl-12 pr-4 py-3 bg-sidebar-bg border border-sidebar-accent rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  />
                </div>
              </div>

              <div className="bg-sidebar-bg rounded-xl shadow-sm border border-sidebar-accent overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-sidebar-accent bg-sidebar-bg">
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                          <div className="flex items-center gap-2">
                            <CategoryIcon
                              fontSize="small"
                              className="text-gray-400"
                            />
                            Nombre
                          </div>
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                          Nombre Normalizado
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                          Acciones
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-sidebar-accent bg-black">
                      {paginatedBrands.length === 0 ? (
                        <tr>
                          <td colSpan="3" className="px-6 py-12 text-center">
                            <CategoryIcon
                              className="mx-auto text-gray-300 mb-3"
                              fontSize="large"
                            />
                            <p className="text-gray-500 text-sm">
                              {search
                                ? "No se encontraron marcas"
                                : "No hay marcas registradas"}
                            </p>
                          </td>
                        </tr>
                      ) : (
                        paginatedBrands.map((brand) => (
                          <tr
                            key={brand._id || brand.name}
                            className="hover:bg-sidebar-accent transition-colors duration-150"
                          >
                            <td className="px-6 py-4">
                              <span className="text-sm font-medium text-white capitalize">
                                {brand.name || "Sin nombre"}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <span className="text-sm text-gray-200 uppercase">
                                {brand.normalizedName || "-"}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => openEditModal(brand)}
                                  className="p-2 text-blue-400 hover:bg-blue-500/10 rounded-lg transition-colors duration-150 border border-transparent hover:border-blue-500/30"
                                  title="Editar marca"
                                >
                                  <EditIcon fontSize="small" />
                                </button>
                                <button
                                  onClick={() => openDeleteModal(brand)}
                                  className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors duration-150 border border-transparent hover:border-red-500/30"
                                  title="Eliminar marca"
                                >
                                  <DeleteIcon fontSize="small" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {filteredBrands.length > 0 && (
                  <div className="px-6 py-4 border-t border-sidebar-accent bg-sidebar-bg">
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-gray-300">
                        Mostrando{" "}
                        <span className="font-semibold text-white">
                          {startIndex + 1}
                        </span>{" "}
                        a{" "}
                        <span className="font-semibold text-white">
                          {Math.min(endIndex, filteredBrands.length)}
                        </span>{" "}
                        de{" "}
                        <span className="font-semibold text-white">
                          {filteredBrands.length}
                        </span>{" "}
                        {filteredBrands.length === 1 ? "marca" : "marcas"}
                      </p>

                      {totalPages > 1 && (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                            disabled={currentPage === 0}
                            className={`p-2 rounded-lg transition-colors ${
                              currentPage === 0
                                ? "text-gray-600 cursor-not-allowed"
                                : "text-gray-300 hover:bg-sidebar-accent hover:text-white"
                            }`}
                          >
                            <ChevronLeftIcon fontSize="small" />
                          </button>

                          <div className="flex items-center gap-1">
                            {Array.from({ length: totalPages }, (_, i) => (
                              <button
                                key={i}
                                onClick={() => setCurrentPage(i)}
                                className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                                  currentPage === i
                                    ? "bg-blue-600 text-white"
                                    : "text-gray-300 hover:bg-sidebar-accent hover:text-white"
                                }`}
                              >
                                {i + 1}
                              </button>
                            ))}
                          </div>

                          <button
                            onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
                            disabled={currentPage === totalPages - 1}
                            className={`p-2 rounded-lg transition-colors ${
                              currentPage === totalPages - 1
                                ? "text-gray-600 cursor-not-allowed"
                                : "text-gray-300 hover:bg-sidebar-accent hover:text-white"
                            }`}
                          >
                            <ChevronRightIcon fontSize="small" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Wrapper>
      </div>

      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-sidebar-bg rounded-xl border border-sidebar-accent w-full max-w-md">
            <div className="px-6 py-4 border-b border-sidebar-accent flex items-center justify-between bg-sidebar-accent/30">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/10 rounded-lg">
                  <AddIcon className="text-blue-400" sx={{ fontSize: 20 }} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Agregar Marca</h3>
                  <p className="text-sm text-gray-400">Crea una nueva marca de vehículo</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setNewBrandName("");
                }}
                className="p-2 hover:bg-sidebar-accent rounded-lg transition-colors"
              >
                <CloseIcon className="text-gray-400" />
              </button>
            </div>

            <div className="p-6">
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Nombre de la Marca *
              </label>
              <input
                type="text"
                value={newBrandName}
                onChange={(e) => setNewBrandName(e.target.value)}
                placeholder="Ej: Toyota, Ford, Chevrolet..."
                className="w-full px-4 py-2 bg-white rounded-lg text-black border-2 border-sidebar-accent focus:border-blue-500 focus:outline-none"
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    handleAddBrand();
                  }
                }}
              />
              <p className="text-xs text-gray-500 mt-2">
                La marca se normalizará automáticamente
              </p>
            </div>

            <div className="px-6 py-4 border-t border-sidebar-accent flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setNewBrandName("");
                }}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleAddBrand}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-2"
              >
                <SaveIcon sx={{ fontSize: 18 }} />
                Crear Marca
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-sidebar-bg rounded-xl border border-sidebar-accent w-full max-w-md">
            <div className="px-6 py-4 border-b border-sidebar-accent flex items-center justify-between bg-sidebar-accent/30">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/10 rounded-lg">
                  <EditIcon className="text-blue-400" sx={{ fontSize: 20 }} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Editar Marca</h3>
                  <p className="text-sm text-gray-400">Modifica el nombre de la marca</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setNewBrandName("");
                  setSelectedBrand(null);
                }}
                className="p-2 hover:bg-sidebar-accent rounded-lg transition-colors"
              >
                <CloseIcon className="text-gray-400" />
              </button>
            </div>

            <div className="p-6">
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Nombre de la Marca *
              </label>
              <input
                type="text"
                value={newBrandName}
                onChange={(e) => setNewBrandName(e.target.value)}
                placeholder="Ej: Toyota, Ford, Chevrolet..."
                className="w-full px-4 py-2 bg-white rounded-lg text-black border-2 border-sidebar-accent focus:border-blue-500 focus:outline-none"
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    handleEditBrand();
                  }
                }}
              />
              <p className="text-xs text-gray-500 mt-2">
                La marca se normalizará automáticamente
              </p>
            </div>

            <div className="px-6 py-4 border-t border-sidebar-accent flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setNewBrandName("");
                  setSelectedBrand(null);
                }}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleEditBrand}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-2"
              >
                <SaveIcon sx={{ fontSize: 18 }} />
                Guardar Cambios
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-sidebar-bg rounded-xl border border-red-500/30 w-full max-w-md">
            <div className="px-6 py-4 border-b border-red-500/30 bg-red-500/10">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-500/20 rounded-lg">
                  <WarningIcon className="text-red-400" sx={{ fontSize: 24 }} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Confirmar Eliminación</h3>
                  <p className="text-sm text-gray-400">Esta acción no se puede deshacer</p>
                </div>
              </div>
            </div>

            <div className="p-6">
              <p className="text-gray-300 mb-4">
                ¿Estás seguro de que deseas eliminar la marca{" "}
                <span className="font-semibold text-white">
                  {selectedBrand?.name}
                </span>
                ?
              </p>
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                <p className="text-sm text-red-400 flex items-start gap-2">
                  <WarningIcon sx={{ fontSize: 16, marginTop: "2px" }} />
                  <span>
                    Se eliminará permanentemente la marca del catálogo.
                  </span>
                </p>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-sidebar-accent flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedBrand(null);
                }}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleDeleteBrand}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center gap-2"
              >
                <DeleteIcon sx={{ fontSize: 18 }} />
                Eliminar Marca
              </button>
            </div>
          </div>
        </div>
      )}

      <Snackbar
        severity={snack.severity}
        message={snack.message}
        visible={snack.visible}
      />
    </div>
  );
};
